/**
 * The conversation list — `docs/activity.md` §7.1 (Board 7, 8 C) and §7.2
 * (Board 16 A): a row per conversation, newest first, 40 at a time.
 *
 * A row: the contact's initials, the name (700 while unread), the company
 * under it — or *not a contact yet* — the channel's glyph, the owner's face
 * when several people are shown, the time (green and 700 while unread), two
 * lines of the last message, one *Attached to* chip, and a green dot while
 * unread. The open one is tinted with a 3 px accent edge.
 *
 * Kept by each person (§7.10): a pin before the time when it is pinned, the
 * viewer's categories beside the chip, and a right-click — or the
 * context-menu key, or Shift+F10 — for the row's menu (`marks.tsx`). On a
 * phone a long press opens the same choices as a sheet.
 */
import {
  useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import type { ConversationRow } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { Face, UnassignedFace, type FacePerson } from '../faces.js'
import { Glyph } from '../glyphs.js'
import { ChannelGlyph, Initials, RecordChip, rowTimeWords } from './look.js'
import { CategoryChip, menuPointFor } from './marks.js'

export interface RowLook {
  today: string
  timeZone: string
  /** More than one person (or Unassigned with others) is shown: rows carry whose they are. */
  several: boolean
  faceOf: (id: string) => FacePerson | null
  phone: boolean
}

/** The row's menu asked for: where (a pointer, or beside the row for a key) and from what. */
export type OnRowMenu = (row: ConversationRow, point: { x: number; y: number }, keyboard: boolean,
  from: HTMLElement) => void

export function ConversationRowView({
  row, open, look, onOpen, onMenu,
}: { row: ConversationRow; open: boolean; look: RowLook; onOpen: () => void; onMenu?: OnRowMenu }) {
  const owner = row.ownerId
    ? look.faceOf(row.ownerId)
      ?? (row.ownerName ? { id: row.ownerId, name: row.ownerName, initials: row.ownerInitials ?? '', colour: null } : null)
    : null
  const sub = row.unknown ? 'not a contact yet' : look.phone ? null : row.company
  const contextMenu = (event: ReactMouseEvent<HTMLButtonElement>) => {
    if (!onMenu) return
    event.preventDefault()
    /* A right button, or a Mac's Control-click, is a pointer; anything else asked from the keyboard. */
    const keyboard = event.button !== 2 && !event.ctrlKey
    onMenu(row, keyboard ? menuPointFor(event.currentTarget) : { x: event.clientX, y: event.clientY }, keyboard,
      event.currentTarget)
  }
  const menuKey = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (!onMenu) return
    if (event.key === 'ContextMenu' || (event.key === 'F10' && event.shiftKey)) {
      event.preventDefault()
      onMenu(row, menuPointFor(event.currentTarget), true, event.currentTarget)
    }
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      onContextMenu={contextMenu}
      onKeyDown={menuKey}
      aria-current={open ? 'true' : undefined}
      aria-keyshortcuts={onMenu ? 'Shift+F10' : undefined}
      data-conversation={row.id}
      data-pinned={row.pinned ? 'true' : undefined}
      /* `leading-[normal]`: Board 7's rows set a line-height only on the preview (1.4). */
      className={cn('flex w-full gap-2.5 border-b border-line-faint px-3.5 py-[11px] text-left leading-[normal]',
        open ? 'bg-green-band-bg shadow-[inset_3px_0_0_rgb(var(--color-accent))]' : 'bg-surface [@media(hover:hover)]:hover:bg-surface-alt')}
    >
      <Initials row={row} size={34} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex items-center gap-1.5">
          <b className={cn('truncate text-[13.5px]', row.unread ? 'font-bold' : 'font-semibold')}>{row.name}</b>
          <ChannelGlyph channel={row.lastChannel} />
          <span className="flex-1" />
          {/* Whose it is, when several are shown — and on one somebody else owns that is here because you follow it (Aaron, Oct 26). */}
          {(look.several || row.following) && (owner
            ? <Face person={owner} size={18} />
            : row.ownerId === null ? <UnassignedFace size={18} /> : null)}
          {row.pinned && (
            <span className="inline-flex text-text-muted" title="Pinned to the top">
              <Glyph name="pushpin" size={12} title="Pinned" />
            </span>
          )}
          <span className={cn('whitespace-nowrap text-[11.5px]',
            row.unread ? 'font-bold text-accent-dark' : 'font-medium text-text-faint')}>
            {rowTimeWords(row.lastAt, look.today, look.timeZone)}
          </span>
        </span>
        {sub && <span className="truncate text-[11.5px] text-text-muted">{sub}</span>}
        <span className={cn('mt-0.5 line-clamp-2 text-[12.5px] leading-[1.4]', row.unread ? 'text-text' : 'text-text-muted')}>
          {row.preview}
        </span>
        {(row.attached || row.categories.length > 0) && (
          <span className="mt-1.5 flex min-w-0 gap-1 overflow-hidden">
            {row.attached && <RecordChip type={row.attached.type} label={row.attached.label} />}
            {row.categories.map((category) => <CategoryChip key={category.id} category={category} />)}
          </span>
        )}
      </span>
      {row.unread && <i aria-label="Unread" className="mt-1.5 block h-2 w-2 shrink-0 rounded-full bg-accent" />}
    </button>
  )
}

/** How far a row slides to show its two buttons (Board 16: *Done* · *Assign to…*). */
const REVEAL = 168

/**
 * A phone row that swipes left (§7.2) — **Done** marks it read for you (on
 * your own conversations and Unassigned ones) and **Assign to…** when allowed.
 */
/** How long a finger rests on a row before its menu opens (§7.10). */
const LONG_PRESS_MS = 500

export function SwipeRow({
  row, look, onOpen, onDone, onAssign, swiped, onSwiped, onLongPress,
}: {
  row: ConversationRow
  look: RowLook
  onOpen: () => void
  onDone: (() => void) | null
  onAssign: (() => void) | null
  swiped: boolean
  onSwiped: (open: boolean) => void
  /** A long press: the row's menu as a sheet (§7.10). */
  onLongPress?: () => void
}) {
  const [dx, setDx] = useState(0)
  const start = useRef<{ x: number; y: number; base: number; moving: boolean } | null>(null)
  const moved = useRef(false)
  const press = useRef<{ timer: number; x: number; y: number } | null>(null)
  const endPress = () => {
    if (press.current) window.clearTimeout(press.current.timer)
    press.current = null
  }
  useEffect(() => endPress, [])
  const actions = (onDone ? 1 : 0) + (onAssign ? 1 : 0)
  const reveal = actions === 2 ? REVEAL : actions === 1 ? REVEAL / 2 : 0

  useEffect(() => { if (!swiped) setDx(0) }, [swiped])

  const down = (event: ReactPointerEvent<HTMLDivElement>) => {
    moved.current = false
    endPress()
    if (onLongPress && !swiped) {
      press.current = {
        x: event.clientX, y: event.clientY,
        timer: window.setTimeout(() => {
          press.current = null
          start.current = null
          /* The lift that follows is not a tap on the row. */
          moved.current = true
          onLongPress()
        }, LONG_PRESS_MS),
      }
    }
    if (reveal === 0) return
    start.current = { x: event.clientX, y: event.clientY, base: swiped ? -reveal : 0, moving: false }
  }
  const move = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (press.current && Math.hypot(event.clientX - press.current.x, event.clientY - press.current.y) > 8) endPress()
    const from = start.current
    if (!from) return
    const x = event.clientX - from.x
    const y = event.clientY - from.y
    if (!from.moving) {
      if (Math.abs(x) < 8) return
      if (Math.abs(y) > Math.abs(x)) { start.current = null; return }
      from.moving = true
      moved.current = true
      event.currentTarget.setPointerCapture(event.pointerId)
    }
    setDx(Math.max(-reveal - 24, Math.min(0, from.base + x)))
  }
  const up = () => {
    endPress()
    const from = start.current
    start.current = null
    if (!from?.moving) return
    const open = dx < -reveal / 2
    setDx(open ? -reveal : 0)
    onSwiped(open)
  }

  return (
    <div className="relative overflow-hidden">
      {reveal > 0 && (
        <div className="absolute inset-y-0 right-0 flex" style={{ width: reveal }} aria-hidden={!swiped}>
          {onAssign && (
            <button type="button" onClick={onAssign} tabIndex={swiped ? 0 : -1}
              className="flex flex-1 items-center justify-center bg-line-soft text-[12.5px] font-semibold text-text">
              Assign to…
            </button>
          )}
          {onDone && (
            <button type="button" onClick={onDone} tabIndex={swiped ? 0 : -1}
              className="flex flex-1 items-center justify-center bg-accent text-[12.5px] font-semibold text-white">
              Done
            </button>
          )}
        </div>
      )}
      <div
        className="relative touch-pan-y"
        style={{ transform: `translateX(${dx}px)`, transition: start.current?.moving ? undefined : 'transform 160ms ease' }}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        /* A long press on a phone browser that offers its own menu offers ours instead. */
        onContextMenu={onLongPress ? (event) => event.preventDefault() : undefined}
        onClickCapture={(event) => {
          if (moved.current) { event.stopPropagation(); event.preventDefault(); moved.current = false; return }
          if (swiped) { event.stopPropagation(); event.preventDefault(); onSwiped(false) }
        }}
      >
        <ConversationRowView row={row} open={false} look={look} onOpen={onOpen} />
      </div>
    </div>
  )
}

/** Loads the next 40 when the end of the list scrolls into view (§7.1). */
export function MoreSentinel({ onMore, busy, root }: { onMore: () => void; busy: boolean; root: HTMLElement | null }) {
  const ref = useRef<HTMLDivElement>(null)
  const more = useRef(onMore)
  more.current = onMore
  useEffect(() => {
    const element = ref.current
    if (!element) return
    const watcher = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) more.current()
    }, { root, rootMargin: '200px' })
    watcher.observe(element)
    return () => watcher.disconnect()
  }, [root])
  return (
    <div ref={ref} className="flex h-12 items-center justify-center text-[12px] text-text-faint">
      {busy ? 'Loading…' : ''}
    </div>
  )
}
